import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsToggle } from './shared/SettingsToggle';
import { Lock, LogOut, AlertTriangle, Check, X, Twitter } from 'lucide-react';
interface AccountSectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const AccountSection: React.FC<AccountSectionProps> = ({
  setHasUnsavedChanges
}) => {
  const [accountInfo, setAccountInfo] = useState({
    username: 'Mitch',
    email: 'mitch.gonnaberich@sinshift.com',
    phone: '+1 (555) 123-4567',
    twitterConnected: true,
    googleConnected: true,
    twoFactorEnabled: false,
    passwordlessLogin: true
  });
  const [sessions, setSessions] = useState([{
    id: 1,
    device: 'Chrome on Windows',
    location: 'Los Angeles, CA',
    lastActive: 'Now',
    current: true
  }, {
    id: 2,
    device: 'Safari on iPhone',
    location: 'Los Angeles, CA',
    lastActive: '2 hours ago',
    current: false
  }, {
    id: 3,
    device: 'Firefox on Mac',
    location: 'San Francisco, CA',
    lastActive: 'Yesterday',
    current: false
  }]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const handleToggleChange = (field: string, value: boolean) => {
    setAccountInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };
  const handleInputChange = (field: string, value: string) => {
    setAccountInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };
  const handleSessionTerminate = (id: number) => {
    setSessions(prev => prev.filter(session => session.id !== id));
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18474">
      <SettingsCard title="Account Information" description="Manage your account details and connected services." data-id="element-18475">
        {/* Username - Read Only */}
        <div className="mb-4" data-id="element-18476">
          <label htmlFor="account-username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18477">
            Username
          </label>
          <input type="text" id="account-username" value={accountInfo.username} readOnly className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 cursor-not-allowed" data-id="element-18478" />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18479">
            Your username cannot be changed.
          </p>
        </div>
        {/* Email */}
        <div className="mb-4" data-id="element-18480">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18481">
            Email Address
          </label>
          <input type="email" id="email" value={accountInfo.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18482" />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18483">
            This email is used for account notifications and recovery.
          </p>
        </div>
        {/* Phone */}
        <div className="mb-4" data-id="element-18484">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18485">
            Phone Number (Optional)
          </label>
          <input type="tel" id="phone" value={accountInfo.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18486" />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18487">
            Used for account recovery and two-factor authentication.
          </p>
        </div>
        {/* Connected Accounts */}
        <div className="mt-6" data-id="element-18488">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-18489">
            Connected Accounts
          </h4>
          <div className="space-y-3" data-id="element-18490">
            {/* Twitter */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-18491">
              <div className="flex items-center gap-3" data-id="element-18492">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-18493">
                  <Twitter size={16} className="text-blue-500" data-id="element-18494" />
                </div>
                <div data-id="element-18495">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18496">
                    Twitter
                  </h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18497">
                    {accountInfo.twitterConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              <button className={`px-3 py-1.5 rounded text-xs font-medium ${accountInfo.twitterConnected ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`} onClick={() => handleToggleChange('twitterConnected', !accountInfo.twitterConnected)} data-id="element-18498">
                {accountInfo.twitterConnected ? 'Disconnect' : 'Connect'}
              </button>
            </div>
            {/* Google */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-18499">
              <div className="flex items-center gap-3" data-id="element-18500">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30" data-id="element-18501">
                  <div size={16} className="text-red-500" data-id="element-18502" />
                </div>
                <div data-id="element-18503">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18504">
                    Google
                  </h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18505">
                    {accountInfo.googleConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              <button className={`px-3 py-1.5 rounded text-xs font-medium ${accountInfo.googleConnected ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`} onClick={() => handleToggleChange('googleConnected', !accountInfo.googleConnected)} data-id="element-18506">
                {accountInfo.googleConnected ? 'Disconnect' : 'Connect'}
              </button>
            </div>
            {/* Connect Another Account */}
            <button className="w-full p-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-18507">
              + Connect another SinShift account
            </button>
          </div>
        </div>
      </SettingsCard>
      <SettingsCard title="Security Settings" description="Manage your account security and login options." data-id="element-18508">
        {/* Change Password */}
        <div className="mb-6" data-id="element-18509">
          <div className="flex items-center justify-between" data-id="element-18510">
            <div data-id="element-18511">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2" data-id="element-18512">
                <Lock size={16} className="text-gray-500" data-id="element-18513" />
                Password
              </h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18514">
                Change your account password.
              </p>
            </div>
            <button className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300" onClick={() => setShowPasswordModal(true)} data-id="element-18515">
              Change Password
            </button>
          </div>
        </div>
        {/* Login Sessions */}
        <div className="mb-6" data-id="element-18516">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-18517">
            Active Sessions
          </h4>
          <div className="space-y-3" data-id="element-18518">
            {sessions.map(session => <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-18519">
                <div data-id="element-18520">
                  <div className="flex items-center gap-2" data-id="element-18521">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18522">
                      {session.device}
                    </h5>
                    {session.current && <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full" data-id="element-18523">
                        Current
                      </span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-18524">
                    {session.location} • Last active: {session.lastActive}
                  </p>
                </div>
                {!session.current && <button className="px-3 py-1.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded text-xs font-medium" onClick={() => handleSessionTerminate(session.id)} data-id="element-18525">
                    Terminate
                  </button>}
              </div>)}
          </div>
        </div>
        {/* Two-Factor Authentication */}
        <div className="mb-4" data-id="element-18526">
          <SettingsToggle id="two-factor" label="Two-Step Authentication" description="Add an extra layer of security to your account with two-factor authentication." checked={accountInfo.twoFactorEnabled} onChange={checked => {
          if (checked) {
            setShowTwoFactorModal(true);
          } else {
            handleToggleChange('twoFactorEnabled', false);
          }
        }} data-id="element-18527" />
        </div>
        {/* Passwordless Login */}
        <div data-id="element-18528">
          <SettingsToggle id="passwordless" label="Passwordless Login" description="Login with a secure link sent to your email instead of entering your password." checked={accountInfo.passwordlessLogin} onChange={checked => handleToggleChange('passwordlessLogin', checked)} data-id="element-18529" />
        </div>
      </SettingsCard>
      <SettingsCard title="Account Management" description="Options for managing your SinShift account." data-id="element-18530">
        <div className="flex items-center justify-between" data-id="element-18531">
          <div data-id="element-18532">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2" data-id="element-18533">
              <AlertTriangle size={16} className="text-red-500" data-id="element-18534" />
              Delete Account
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18535">
              Permanently delete your SinShift account and all associated data.
            </p>
          </div>
          <button className="px-3 py-1.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded text-sm font-medium" onClick={() => setShowDeleteModal(true)} data-id="element-18536">
            Delete Account
          </button>
        </div>
      </SettingsCard>
      {/* Delete Account Modal */}
      {showDeleteModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-18537">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-18538">
            <div className="flex items-center gap-3 mb-4" data-id="element-18539">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30" data-id="element-18540">
                <AlertTriangle size={20} className="text-red-500" data-id="element-18541" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-18542">
                Delete Account
              </h3>
            </div>
            <p className="mb-4 text-gray-700 dark:text-gray-300" data-id="element-18543">
              Are you sure you want to delete your account? This action is
              permanent and cannot be undone. All your data, including profile
              information, messages, and content will be permanently deleted.
            </p>
            <div className="flex justify-end gap-3" data-id="element-18544">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowDeleteModal(false)} data-id="element-18545">
                Cancel
              </button>
              <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm" onClick={() => {
            // Handle account deletion
            setShowDeleteModal(false);
          }} data-id="element-18546">
                Delete Account
              </button>
            </div>
          </div>
        </div>}
      {/* Change Password Modal */}
      {showPasswordModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-18547">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-18548">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" data-id="element-18549">
              Change Password
            </h3>
            <div className="space-y-4" data-id="element-18550">
              <div data-id="element-18551">
                <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18552">
                  Current Password
                </label>
                <input type="password" id="current-password" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-18553" />
              </div>
              <div data-id="element-18554">
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18555">
                  New Password
                </label>
                <input type="password" id="new-password" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-18556" />
              </div>
              <div data-id="element-18557">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18558">
                  Confirm New Password
                </label>
                <input type="password" id="confirm-password" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-18559" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-18560">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowPasswordModal(false)} data-id="element-18561">
                Cancel
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg text-sm" onClick={() => {
            // Handle password change
            setShowPasswordModal(false);
          }} data-id="element-18562">
                Update Password
              </button>
            </div>
          </div>
        </div>}
      {/* Two-Factor Authentication Modal */}
      {showTwoFactorModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-18563">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-18564">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" data-id="element-18565">
              Set Up Two-Factor Authentication
            </h3>
            <div className="space-y-4" data-id="element-18566">
              <p className="text-gray-700 dark:text-gray-300 text-sm" data-id="element-18567">
                Scan this QR code with your authenticator app (like Google
                Authenticator or Authy) to set up two-factor authentication.
              </p>
              <div className="flex justify-center p-4" data-id="element-18568">
                <div className="w-48 h-48 bg-white p-4 rounded-lg" data-id="element-18569">
                  {/* Placeholder for QR code */}
                  <div className="w-full h-full border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-18570">
                    <span className="text-gray-400 text-xs" data-id="element-18571">QR Code</span>
                  </div>
                </div>
              </div>
              <div data-id="element-18572">
                <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18573">
                  Verification Code
                </label>
                <input type="text" id="verification-code" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" placeholder="Enter 6-digit code" data-id="element-18574" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-18575">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => {
            setShowTwoFactorModal(false);
          }} data-id="element-18576">
                Cancel
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg text-sm" onClick={() => {
            // Handle 2FA setup
            handleToggleChange('twoFactorEnabled', true);
            setShowTwoFactorModal(false);
          }} data-id="element-18577">
                Verify & Enable
              </button>
            </div>
          </div>
        </div>}
    </div>;
};